import System from "../System";
import AudioClip, { AudioTrackType } from "../Audio/AudioClip";
import { IsMobile } from "Utility/Platform";
import { IGameState } from "State/Game/IGameState";
import { StopMusic } from "./AudioHelper";
import { Howler } from 'howler';

export enum AudioSystemEvent {
    PLAY = 'play_audio',
    STOP = 'stop_audio',
    VOLUME = 'set_volume',
}

// WARNING: these values are also used as storage key
// Warning: values of this enum are also used as localization keys
// TODO: Replace enum usage and/or string-building for asset references.
export enum AudioStreamType {
    MUSIC = 'audioMusic',
    SFX = 'audioSfx',
}

export default class AudioSystem extends System {
    public audioClips: AudioClip[];

    public audioEnabled: boolean = true;
    public windowHasFocus: boolean = true;
    public SFXVolume: number = 0;
    public MusicVolume: number = 0;
    public currentMusic: AudioClip;

    protected isFrozen: boolean = false;

    private findClipFromString(_clip: string): AudioClip | null {
        if (!_clip) return null;
        return this.audioClips.find(
            (e: AudioClip) => e.name === _clip
        ) || null;
    }

    public init() {
        this.audioClips = [];

        this.scene.on(AudioSystemEvent.PLAY, (_clip: AudioClip) => {
            // Prevent playing multiple instances of a clip if `allowMultipleInstances` is `true`.
            if (this.findClipFromString(_clip.name) && !_clip.allowMultipleInstances) return;

            this.playAudio(_clip);
        });
        this.scene.on(AudioSystemEvent.STOP, (_clip: AudioClip | string) => {
            if (_clip instanceof AudioClip) {
                this.destroyAudioClip(_clip);
            } else {
                const clip: AudioClip = this.findClipFromString(_clip);
                if (clip) {
                    this.destroyAudioClip(clip);
                } else {
                    console.warn(`Failed to find clip to stop: "${_clip}"`);
                }
            }
        });
        this.scene.on(AudioSystemEvent.VOLUME, (_clip: AudioClip | string, _volume: number) => {
            if (_clip instanceof AudioClip) {
                _clip.volume = _volume;
            } else {
                const clip: AudioClip = this.findClipFromString(_clip);
                if (clip) {
                    clip.volume = _volume;
                } else {
                    console.warn(`Failed to find clip to change volume: "${_clip}"`);
                }
            }
        });

        if (window) {
            window.addEventListener('pagehide', this.checkFocus.bind(this));
            window.addEventListener('pageshow', this.checkFocus.bind(this));
            window.addEventListener('focus', this.checkFocus.bind(this));
            window.addEventListener('blur', this.checkFocus.bind(this));

            window.onblur = this.checkFocus.bind(this);
            window.onfocus = this.checkFocus.bind(this);
        }
        document.addEventListener('pagehide', this.checkFocus.bind(this));
        document.addEventListener('pageshow', this.checkFocus.bind(this));
        document.addEventListener('focus', this.checkFocus.bind(this));
        document.addEventListener('blur', this.checkFocus.bind(this));
        document.addEventListener(
            'visibilitychange',
            this.checkFocus.bind(this)
        );

        document.onfocus = this.checkFocus.bind(this);
        document.onblur = this.checkFocus.bind(this);

        this.checkFocus();
    }

    public handleStateChanged(state: IGameState): void {
        if (state.playerState.settings === null) return;
        this.audioEnabled = state.context.audioEnabled;
        this.SFXVolume = Number(state.playerState.settings.audioSfx);
        this.MusicVolume = Number(state.playerState.settings.audioMusic);

        this.handleAudio();
    }

    private isMuted() {
        return this.audioEnabled === false || this.windowHasFocus === false;
    }

    private handleAudio() {
        //@ts-ignore
        if (window.isSoundFrozen) return;

        Howler.mute(this.isMuted());

        this.audioClips.forEach(clip => {
            clip.volume = this.getClipVolume(clip);
        });
    }

    private getClipVolume(clip: AudioClip): number {
        switch (clip.track) {
            case AudioTrackType.SFX:
                return this.SFXVolume;
            case AudioTrackType.MUSIC:
                return this.MusicVolume;
        }

        return 0;
    }

    private checkFocus() {
        if (IsMobile()) {
            this.windowHasFocus = document.visibilityState === 'visible';
        } else {
            this.windowHasFocus =
                document.visibilityState === 'visible' && document.hasFocus();
        }
        if (!this.isFrozen) {
            this.handleAudio();
        }
    }

    public onDestroy(): void {
        this.audioClips.slice(0).forEach(this.destroyAudioClip.bind(this));
        this.audioClips = [];

        this.scene.off(AudioSystemEvent.PLAY);
        this.scene.off(AudioSystemEvent.STOP);
    }

    public playAudio(clip: AudioClip) {
        this.audioClips.push(clip);
        clip.onComplete(this.destroyAudioClip.bind(this));
        clip.volume = this.getClipVolume(clip);
        clip.play();

        if (clip.track === AudioTrackType.MUSIC) {
            if (this.currentMusic) {
                const oldMusic = this.currentMusic;
                oldMusic.howl.fade(oldMusic.volume, 0, 500);
                oldMusic.howl.on('fade', () => StopMusic(oldMusic));
                clip.howl.fade(0, clip.volume, 500);
            }
            this.currentMusic = clip;
        }
    }

    private destroyAudioClip(clip: AudioClip) {
        const index = this.audioClips.indexOf(clip);
        if (index >= 0) this.audioClips.splice(index, 1);

        clip.destroy();
    }

    protected onLateUpdate(): void { }

    protected onStart(): void { }

    protected onStop(): void { }

    protected onUpdate(): void { }

    async preload(): Promise<any> {
        return undefined;
    }
}
